/**
 * Net30PipelineWidget
 * -------------------
 * Triage widget for the Admin dashboard home. Surfaces every hospital with
 * net30_status === "requested", sorted by days-waiting (oldest first) so
 * stale approvals bubble to the top. Each row has one-click Approve /
 * Decline buttons (powered by the existing net30_factoring endpoints) and
 * a link to drill into the hospital profile if the approver wants more
 * context.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Banknote,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Net30PipelineWidget({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState("");

  const fetchPipeline = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/net30/pipeline`, { headers });
      setData(res.data);
    } catch (e) {
      // Stay quiet on dashboard so it never blocks the page
      setData({ items: [], count: 0, can_approve: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPipeline();
    // Refresh every 60s so newly-requested hospitals show up without a hard refresh
    const interval = setInterval(fetchPipeline, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (item, action) => {
    setBusyId(item.id);
    try {
      await axios.post(
        `${API}/api/admin/health-systems/${item.id}/net30/${action}`,
        { notes: decisionNotes || (action === "approve" ? "Approved via dashboard triage" : "Declined via dashboard triage") },
        { headers }
      );
      toast.success(
        action === "approve"
          ? `${item.name} approved for Net 30`
          : `${item.name} declined`
      );
      setConfirmingId(null);
      setDecisionNotes("");
      fetchPipeline();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Net 30 pipeline…
        </div>
      </div>
    );
  }

  // If nothing pending, show a compact "all clear" state — keeps the widget
  // a calm presence on the dashboard until something needs attention.
  if (!data || data.count === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="net30-pipeline-widget">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Banknote className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Net 30 Pipeline</h3>
              <p className="text-xs text-slate-500">Hospitals awaiting Flash Funding approval</p>
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="w-3 h-3 mr-1" /> All clear
          </Badge>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          No hospitals are waiting on Net 30 approval. New requests will appear here automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="net30-pipeline-widget">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
            <Banknote className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Net 30 Pipeline</h3>
            <p className="text-xs text-slate-500">Hospitals awaiting Flash Funding approval</p>
          </div>
        </div>
        <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="net30-pipeline-count">
          <Clock className="w-3 h-3 mr-1" /> {data.count} pending
        </Badge>
      </div>

      <div className="space-y-2">
        {data.items.slice(0, 5).map((item) => {
          const isConfirming = confirmingId === item.id;
          const isBusy = busyId === item.id;
          const urgent = item.days_waiting >= 7;
          return (
            <div
              key={item.id}
              className={`rounded-lg border p-3 transition-colors ${
                urgent ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-slate-50/40"
              }`}
              data-testid={`net30-pipeline-row-${item.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to="/admin/hospitals"
                      className="font-semibold text-slate-900 hover:text-blue-600 truncate"
                      title={item.legal_entity_name || item.name}
                    >
                      {item.name}
                    </Link>
                    {urgent && (
                      <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">
                        Urgent
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {item.days_waiting === 0 ? "today" : `${item.days_waiting}d waiting`}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {item.city && <span>{item.city}{item.state ? `, ${item.state}` : ""}</span>}
                    {item.ap_contact_email && <span>AP: {item.ap_contact_email}</span>}
                    {item.estimated_monthly_volume && <span>Vol: {item.estimated_monthly_volume}</span>}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Requested by {item.requested_by} ·{" "}
                    {item.requested_at && new Date(item.requested_at).toLocaleDateString()}
                  </p>
                </div>
                {!isConfirming && data.can_approve && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => setConfirmingId(item.id)}
                      className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs"
                      data-testid={`net30-pipeline-approve-${item.id}`}
                      disabled={isBusy}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmingId(`${item.id}:decline`)}
                      className="h-7 px-2 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`net30-pipeline-decline-${item.id}`}
                      disabled={isBusy}
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Decline
                    </Button>
                  </div>
                )}
                {!data.can_approve && (
                  <Link
                    to="/admin/hospitals"
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600"
                  >
                    View <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
              </div>

              {(isConfirming || confirmingId === `${item.id}:decline`) && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                  <textarea
                    rows={2}
                    value={decisionNotes}
                    onChange={(e) => setDecisionNotes(e.target.value)}
                    placeholder={
                      confirmingId === `${item.id}:decline`
                        ? "Reason (e.g. credit declined by Flash)"
                        : "e.g. Flash approved $75k credit limit"
                    }
                    className="w-full text-xs border border-slate-300 rounded p-2"
                    data-testid={`net30-pipeline-notes-${item.id}`}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setConfirmingId(null); setDecisionNotes(""); }}
                      className="h-7 px-2 text-xs"
                      disabled={isBusy}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        decide(item, confirmingId === `${item.id}:decline` ? "decline" : "approve")
                      }
                      className={`h-7 px-3 text-xs ${
                        confirmingId === `${item.id}:decline`
                          ? "bg-rose-600 hover:bg-rose-700"
                          : "bg-emerald-600 hover:bg-emerald-700"
                      }`}
                      disabled={isBusy}
                      data-testid={`net30-pipeline-confirm-${item.id}`}
                    >
                      {isBusy ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : confirmingId === `${item.id}:decline` ? (
                        <XCircle className="w-3 h-3 mr-1" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      )}
                      Confirm {confirmingId === `${item.id}:decline` ? "decline" : "approve"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.count > 5 && (
        <Link
          to="/admin/hospitals"
          className="flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-3 pt-2 border-t border-slate-100"
        >
          <Sparkles className="w-3 h-3" />
          See all {data.count} pending in Hospital Network <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}
